import { WebsocketEvents, WebsocketEventTypes } from "./types";

// Keepalive. Signage sockets are idle for hours at a time, and an idle TCP flow
// gets silently reaped by NAT/CGNAT gear in the venues our screens live in —
// silently meaning neither end sees a FIN, so `onclose` never fires and the
// player sits "connected" forever without receiving anything.
//
// The server answers these pings from its Durable Object via
// setWebSocketAutoResponse, so they never wake it from hibernation.
const PING_MESSAGE = "ping";
const PONG_MESSAGE = "pong";
const PING_INTERVAL_MS = 25_000;
// Force a reconnect once this long has passed with no pong. Three missed pings.
const PONG_TIMEOUT_MS = 80_000;

class CustomWebSocket {
  private ws: WebSocket;
  private pingTimer?: ReturnType<typeof setInterval>;
  private lastPongAt = Date.now();

  public onOpen: ((this: WebSocket, ev: Event) => any) | null;
  public onMessage: ((this: WebSocket, ev: MessageEvent) => any) | null;
  public onError: ((this: WebSocket, ev: Event) => any) | null;
  public onClose: ((this: WebSocket, ev: CloseEvent) => any) | null;

  // Called by the owner of this socket before the public handlers run, so
  // reconnect bookkeeping can't be clobbered by consumer code.
  public onOpenInternal: (() => void) | null = null;

  public onCallback: {
    [x: string]: (data?: any) => void;
  } = {};

  public get isConnecting() {
    return this.ws.readyState === WebSocket.CONNECTING;
  }

  public get isOpen() {
    return this.ws.readyState === WebSocket.OPEN;
  }

  constructor(url: string, _opts?: Record<string, any>) {
    this.ws = new WebSocket(url);

    this.onOpen = this.ws.onopen;
    this.onMessage = this.ws.onmessage;
    this.onError = this.ws.onerror;
    this.onClose = this.ws.onclose;

    this.ws.onopen = (ev) => {
      this.lastPongAt = Date.now();
      this.startKeepalive();
      this.onOpenInternal?.();
      this.onOpen?.call(this.ws, ev);
    };
    this.ws.onmessage = this.onMessageCallback;
    this.ws.onerror = (ev) => {
      this.onError?.call(this.ws, ev);
    };
    this.ws.onclose = (ev) => {
      this.stopKeepalive();
      this.onClose?.call(this.ws, ev);
    };
  }

  private startKeepalive() {
    this.stopKeepalive();

    this.pingTimer = setInterval(() => {
      // No pong in three intervals means the connection is a zombie. Closing it
      // turns an invisible failure into an `onclose`, which is what drives the
      // reconnect.
      if (Date.now() - this.lastPongAt > PONG_TIMEOUT_MS) {
        this.stopKeepalive();
        try {
          this.ws.close(4000, "Keepalive timeout");
        } catch (_e) {
          /* the socket is already gone; onclose still fires */
        }
        return;
      }

      if (this.ws.readyState !== WebSocket.OPEN) return;

      try {
        this.ws.send(PING_MESSAGE);
      } catch (_e) {
        /* send on a dying socket; the timeout above handles recovery */
      }
    }, PING_INTERVAL_MS);
  }

  private stopKeepalive() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = undefined;
    }
  }

  public close() {
    this.stopKeepalive();
    this.ws.close();
  }

  onMessageCallback = (ev: MessageEvent) => {
    // The keepalive reply is a bare string, not JSON. Handle it before anything
    // else so it never reaches application code.
    if (ev.data === PONG_MESSAGE) {
      this.lastPongAt = Date.now();
      return;
    }

    try {
      const data = JSON.parse(ev.data);
      const type = data?.type ?? "";
      const event = data?.event ?? "";

      if (type !== "callback") {
        this.onMessage?.call(this.ws, ev);
      } else {
        // Guard the lookup: a callback frame for an event with no registered
        // handler used to throw and get mis-routed into onMessage.
        if (event && typeof this.onCallback[event] === "function") {
          this.onCallback[event](data.data);
        }
      }
    } catch (_e) {
      this.onMessage?.call(this.ws, ev);
    }
  };

  public emit<T extends keyof typeof WebsocketEvents>(
    event: T,
    data: WebsocketEventTypes[T]["request"],
    cb?: (data?: WebsocketEventTypes[T]["response"]) => void,
  ) {
    if (cb) this.onCallback[event] = cb;

    if (this.ws.readyState !== WebSocket.OPEN) return;

    this.ws.send(
      JSON.stringify({
        event: event,
        data: data,
      }),
    );
  }
}

export default CustomWebSocket;
